/* eslint-disable arrow-body-style */
import * as React from "react";
import {autorun} from "mobx";
import {observer} from "mobx-react";
import {Message} from "semantic-ui-react";

import "./project-perimeter-editor.scss";

import {AppState} from "../../app-mobx";
import {MapState} from "../../../../core/map-state";

import {RisikoanalyseStore} from "../../../../stores/risikoanalyse-store-mobx";

import {UiState} from "../../../../core/ui-state";
import {EditorConfiguration} from "../../../../core/config/editor-configuration";

import Handles from "@arcgis/core/core/Handles";
import {MapViewerConfiguration} from "../../../../config/map-viewer-configuration";

import {ApiLayerConfiguration} from "../../../../config/api-configuration";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriSketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import {
    AddButton,
    ClearButton
} from "../project-perimeter/project-perimeter-editor-tools/project-perimeter-editor-tools";
import FeatureEffect from "@arcgis/core/layers/support/FeatureEffect";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import EsriFeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";


export enum EditorMode {
    PerimeterDrawing = "perimeterDrawing",
    PerimeterSelector = "perimeterSelector",
}

// TODO: auf react context umstellen
export interface ProjectPerimeterEditorProps {
    appStore: AppState;
    uiStore: UiState;
    config: EditorConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    mapStore: MapState;
    keyAppend?: string | number | undefined;
    layerInfos?: any;
    small?: boolean;
    risikoanalyse: RisikoanalyseStore;
    layers: ApiLayerConfiguration[];
}

/**
 * Editor to draw polygon and filter features belonging to the selected geometry
 */
@observer
export class ProjectPerimeterEditor extends React.Component<ProjectPerimeterEditorProps, any> {
    watchHandles: any[] = [];
    sketchWatchHandle: any;
    autorunDisposers: any[] = [];
    handles: Handles = new Handles();
    widgetKey = "project-perimeter-editor";
    editorRef: any;

    sketchLayer: EsriGraphicsLayer;
    proposalLayer: EsriGraphicsLayer;
    highlightLayer: EsriGraphicsLayer;
    sketchViewModel: EsriSketchViewModel;

    constructor(props: ProjectPerimeterEditorProps) {
        super(props);
        this.state = {
            postMessages: [],
            preMessages: [],
            visible: true,
            isLoading: false,
            perimeter: null,  // __esri.Graphic[]
            editor: null,  // editor
            viewModel: null,  // editor viewModel
            mode: EditorMode.PerimeterDrawing,
            sketchViewModelReady: false
        };
        this.editorRef = React.createRef();
        // this.handleEsc = this.handleEsc.bind(this);

        this.sketchLayer = new EsriGraphicsLayer({listMode: "hide", title: "sketchLayer 1", opacity: 1 });
        this.proposalLayer = new EsriGraphicsLayer({listMode: "hide", title: "proposalLayer 1", opacity: 1 });
        this.highlightLayer = new EsriGraphicsLayer({listMode: "hide", title: "highlightLayer 1"});
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((loadingReaction: any) => {
            const {risikoanalyse, mapStore, uiStore} = this.props;

            if (!risikoanalyse.apiLoaded) {
                return null;
            }

            if (uiStore.widgets.has(this.widgetKey)) {
                loadingReaction.dispose(); // stop autorun
                return;
            }
            if (!mapStore.map || !mapStore.view) {
                return;
            }
            loadingReaction.dispose();

            this.setState({
                isLoading: false
            });

            // disable popup
            uiStore.setPopupDisabled(true);

            uiStore.addWidget(this.widgetKey, this);

            // add proposal layer to the map
            if (this.proposalLayer && !mapStore?.map.layers.includes(this.proposalLayer)) {
                mapStore?.map.add(this.proposalLayer);
            }
            // add sketch layer to the map
            if (this.sketchLayer && !mapStore?.map.layers.includes(this.sketchLayer)) {
                mapStore?.map.add(this.sketchLayer);
            }
            // add highlight layer
            if (this.highlightLayer && !mapStore?.map.layers.includes(this.highlightLayer)) {
                mapStore?.map.add(this.highlightLayer);
            }

            this.clearHighlights();

            this.initSketchViewModel();

            // add ESC key handler
            // document.addEventListener("keydown", this.handleEsc, false);
        }));
    }

    componentDidUpdate() {}

    componentWillUnmount(): void {
        const {uiStore} = this.props;

        this.deactivatePerimeterDrawing();

        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        // remove sketch watch handle
        this.sketchWatchHandle?.remove();
        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;
        this.handles.removeAll();

        // remove esc handler
        // document.removeEventListener("keydown", this.handleEsc, false);

        // enable popup
        uiStore.setPopupDisabled(false);

        uiStore.removeWidget(this.widgetKey);
    }

    render() {
        const {risikoanalyse} = this.props;
        const {postMessages, preMessages, mode, sketchViewModelReady} = this.state;

        if (!sketchViewModelReady) {
            return "";
        }

        const hasPerimeter = risikoanalyse.perimeterGeometry !== null;

        const editor: any[] = [];
        editor.push(
            <div className="project-perimeter-editor" key={this.widgetKey}>
                <Message
                    attached info size={"mini"}
                    header="Bereich einzeichnen"
                />
                <div className="project-perimeter-tools">
                    <AddButton
                        active={mode === EditorMode.PerimeterDrawing}
                        onAdd={() => {
                            this.activatePerimeterDrawing();
                        }}
                    />
                    {hasPerimeter &&
                        <>
                            <ClearButton onClear={() => {
                                this.clearPerimeterSketch();
                                this.clearHighlights();
                                this.clearProposal();
                                this.removeLayerEffects();
                                this.removeFilterForFeatureTable();
                            }} />
                        </>
                    }
                </div>
            </div>
        );

        this.editorRef.current = editor[0];

        return (
            <div className={"geo-project-perimeter-editor"}>
                {preMessages}
                {editor}
                {postMessages}
                <div className="geo-project-perimeter-editor-footer"/>
            </div>
        );
    }

    private initSketchViewModel(): void {
        const {risikoanalyse,  mapStore, mapViewerConfig} = this.props;

        // initialize model to draw a selection box
        this.sketchViewModel = new EsriSketchViewModel({
            view: mapStore?.view,
            layer: this.sketchLayer,
            tooltipOptions: {
                enabled: true
            },
            defaultCreateOptions: {
                mode: "hybrid"
            },
            defaultUpdateOptions: {
                tool: "reshape",
                enableRotation: false,
                enableScaling: false,
                reshapeOptions: {
                    shapeOperation: "none"
                }
            },
            polygonSymbol: mapViewerConfig.symbols["perimeterProposal"] as __esri.SimpleFillSymbol
        });

        const readyHandle = reactiveUtils.watch(
            () => this.sketchViewModel.state === "ready",
            (isReady) => {
                this.setState({
                    sketchViewModelReady: isReady
                });
            },
            {once: true, initial: true}
        );
        this.watchHandles.push(readyHandle);

        // watch changes in application edit mode
        this.autorunDisposers.push(autorun((editingReaction: any) => {
            if(!risikoanalyse.isEditingProjectPerimeter) {
                editingReaction.dispose();
                // deactivate perimeter selector when editing mode is disabled
                this.deactivatePerimeterDrawing();
            }
        }));
    }

    /**
     * Activate tool to draw a perimeter
     * @private
     */
    private activatePerimeterDrawing() {
        const {risikoanalyse, uiStore} = this.props;

        if (!this.sketchViewModel) {
            throw new Error("ProjectPerimeterEditor: SketchViewModel not initialized.");
        }
        this.sketchWatchHandle?.remove();

        this.clearPerimeterSketch();

        this.setState({
            mode: EditorMode.PerimeterDrawing
        });

        this.sketchWatchHandle =
            this.sketchViewModel.on("create", async (event) => {
                if (event.state === "complete") {
                    // this polygon will be used to query features that intersect it
                    const geometries = this.sketchLayer.graphics.map((graphic: __esri.Graphic) => {
                        return graphic.geometry;
                    });
                    const queryGeometry = geometryEngine.union(geometries.toArray());
                    this.setLayerEffects(queryGeometry);
                    risikoanalyse.setPerimeterGeometry(queryGeometry);
                }
            });

        // activate tool to draw rectangle
        this.sketchViewModel.create("polygon");

        // update geometry
        this.sketchWatchHandle =
            this.sketchViewModel.on("update", async (event) => {
                if (event.state === "start" || event.state === "active") {
                    const geometries = this.sketchLayer.graphics.map(function (graphic: __esri.Graphic) {
                        return graphic.geometry;
                    });
                    const queryGeometry = geometryEngine.union(geometries.toArray());
                    this.setLayerEffects(queryGeometry);
                    risikoanalyse.setPerimeterGeometry(queryGeometry);

                    // // TODO GRK: show graphic??
                    // risikoanalyse.LeitartStore.layer.popupTemplate.title = "Analysedaten";
                    // risikoanalyse.LeitartStore.layer.popupTemplate.content = "<div>show MediaContent here</div>";
                    risikoanalyse.PopupStore.setVisible(true);
                }
            });

        this.sketchWatchHandle =
            this.sketchViewModel.on("delete", async () => {
                risikoanalyse.setPerimeterGeometry(null);
            });
    }

    private removeFilterForFeatureTable() {
        const {risikoanalyse} = this.props;

        risikoanalyse.clearPerimeterGeometry();
    }

    // private handleEsc(event: any): void {
    //     if (event.key === "Escape") {
    //         this.cancelEditing();
    //     }
    //     if (event.key === "+") {  }
    // }

    private deactivatePerimeterDrawing() {
        // this.setState({
        //     updatePerimeter: null
        // });
        this.clearPerimeterSketch();
        this.sketchViewModel.cancel();
        this.sketchWatchHandle?.remove();
        this.clearProposal();
    }

    /**
     * Clear highlight layers
     * @private
     */
    private clearHighlights(): void {
        this.highlightLayer?.removeAll();
    }

    /**
     * Clear selection layers
     * @private
     */
    private clearPerimeterSketch(): void {
        this.sketchLayer?.removeAll();
    }

    /**
     * Clear proposal layers
     * @private
     */
    private clearProposal(): void {
        this.proposalLayer?.removeAll();
    }

    // TODO GRK: invoke?
    private disableClearButton(): void {
        const {risikoanalyse} = this.props;
        console.log("*** disable clear");
        risikoanalyse.clearPerimeterGeometry();
    }

    private setLayerEffects(queryGeometry: __esri.Geometry) {
        const {risikoanalyse} = this.props;

        // apply a feature effect to features that do not
        // meet the filter requirements
        const featureFilter = new FeatureFilter({
            geometry: queryGeometry,
            spatialRelationship: "intersects",
            distance: 100,
            units: "meters"
        });

        const effectLayers = [
            risikoanalyse.LeitartStore.layerView,
            risikoanalyse.AlleArtenStore.layerView
        ];

        // set effect on excluded features
        // make them gray and transparent
        // set effect on both layers -> topic switch can happen afterward
        effectLayers.forEach((layerView: EsriFeatureLayerView) => {
            layerView.featureEffect = new FeatureEffect({
                filter: featureFilter,
                excludedEffect: "grayscale(70%) opacity(50%) drop-shadow(0, 0px, 12px)",
            });
        });
    }

    private removeLayerEffects() {
        const {risikoanalyse} = this.props;

        const effectLayers = [
            risikoanalyse.LeitartStore.layerView,
            risikoanalyse.AlleArtenStore.layerView
        ];

        effectLayers.forEach((layerView: EsriFeatureLayerView) => {
            layerView.featureEffect = new FeatureEffect();
        });
    }
}
